import React, { useEffect, useRef } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';

import {
    setupLobbyAudioStream,
    setupLobbyVideoStream,
    stopLobbyAudioStream,
    stopLobbyVideoStream,
} from 'flow/actions/meeting-lobby-actions';
import Icon from 'flow/components/ui-components/Icon';
import PopoverMenu from 'flow/components/PopoverMenu';
import Tooltip from 'flow/components/ui-components/Tooltip';
import useAppContext from 'flow/hooks/useAppContext';

import { MeetingLocation } from 'flow/utils/meeting-constants';
import MeetingAudioIndicator from 'flow/components/meeting/shared/MeetingAudioIndicator';
import MeetingDeviceSettings from 'flow/components/meeting/shared/MeetingDeviceSettings';
import MeetingLobbyAdmissionNotice from './MeetingLobbyAdmissionNotice';
import { selectOwnWaitingRoomStanding } from 'flow/selectors/video-selectors';
import MeetingLobbyDeviceControls from './MeetingLobbyDeviceControls';
import { LOBBY_CONTROL_CLASS } from './MeetingLobbyDeviceButton';
import { addMeetingLog } from 'flow/utils/meeting-utils';

// no-dark, as the meeting canvas and every other deliberately dark surface
// carries: this is a dark frame with white controls on it, so its colours are
// the design rather than the theme's. Without the pin, gray-800 and white swap
// under html.dark and the preview comes out light with black controls — the
// design inside out. The device-settings popover portals to #contextMenuPortal,
// so it escapes this and still follows the theme, as it should.
//
// The box keeps the meeting's shape whatever the camera is doing, so nothing
// moves when a feed arrives, leaves, or takes a couple of frames to decode.
// Matches the tiles, where the layout decides the box and the feed cover-crops
// into it — so this preview is framed the way others will actually see you.
// Above `md` the fixed 640x360 video already sets it, hence aspect-auto there.
const VIDEO_CONTAINER_CLASS =
    'no-dark relative flex aspect-video rounded-2xl bg-gray-800 md:aspect-auto';

const MeetingLobbyVideo = () => {
    const { i18next } = useAppContext();
    const deviceSettingsTitle = i18next.t(
        'videoconference.lobby.device_settings.title',
    );
    const dispatch = useAppDispatch();

    const audioActive = useAppSelector((state) =>
        state.meeting.getIn(['lobby', 'audioActive']),
    );
    const videoActive = useAppSelector((state) =>
        state.meeting.getIn(['lobby', 'videoActive']),
    );
    const hasActiveMeeting = useAppSelector(
        (state) => !!state.meeting.getIn(['activeMeeting', 'meetingCode']),
    );
    const lobbyVideoDeviceInUse = useAppSelector((state) =>
        state.meeting.getIn(['lobby', 'deviceInUse', 'video']),
    );
    const lobbyMicDeviceInUse = useAppSelector((state) =>
        state.meeting.getIn(['lobby', 'deviceInUse', 'mic']),
    );
    const videoStream = useAppSelector((state) =>
        state.meeting.getIn(['lobby', 'videoStream']),
    );
    const audioStream = useAppSelector((state) =>
        state.meeting.getIn(['lobby', 'audioStream']),
    );
    const cameraError = useAppSelector((state) =>
        state.meeting.getIn(['lobby', 'cameraError']),
    );
    const videoAllowed = useAppSelector((state) =>
        state.meeting.getIn(['devices', 'videoAllowed']),
    );
    const ownWaitingRoomStanding = useAppSelector(selectOwnWaitingRoomStanding);

    const videoRef = useRef(null);

    const lobbyVideoDeviceId = lobbyVideoDeviceInUse?.deviceId;
    useEffect(() => {
        let cancelled = false;

        if (!hasActiveMeeting && lobbyVideoDeviceId) {
            (async () => {
                await dispatch(stopLobbyVideoStream());
                if (cancelled) {
                    return;
                }
                await dispatch(setupLobbyVideoStream(lobbyVideoDeviceId));
            })().catch((error) => {
                if (!cancelled) {
                    addMeetingLog('Error setting up video stream:', error);
                }
            });
        }

        return () => {
            cancelled = true;
            dispatch(stopLobbyVideoStream());
        };
    }, [dispatch, lobbyVideoDeviceId, hasActiveMeeting]);

    useEffect(() => {
        let cancelled = false;

        const asyncSetupLobbyAudioStream = async () => {
            if (hasActiveMeeting) {
                return;
            }

            if (lobbyMicDeviceInUse?.deviceId) {
                dispatch(setupLobbyAudioStream(lobbyMicDeviceInUse.deviceId));
            }
        };

        asyncSetupLobbyAudioStream().catch((error) => {
            if (!cancelled) {
                addMeetingLog('Error setting up audio stream:', error);
            }
        });

        return () => {
            cancelled = true;
            dispatch(stopLobbyAudioStream());
        };
    }, [dispatch, lobbyMicDeviceInUse, hasActiveMeeting]);

    // Attached imperatively, and only when the stream itself changes. Assigning
    // srcObject re-runs the media element load algorithm even when the value is
    // identical, which drops the current frame and the element's intrinsic size
    // until the next one decodes — visible below `md`, where the layout follows
    // that size rather than a fixed box. Muting has no business doing that, so
    // audioStream is deliberately not a dependency here.
    useEffect(() => {
        if (videoRef.current && videoRef.current.srcObject !== videoStream) {
            videoRef.current.srcObject = videoStream;
        }
    }, [videoStream]);

    // Ordered, and only ever one: without permission there is nothing to report
    // about the camera being off or failing, and the door's answer outranks all
    // of it — both are centred over the preview, so anything said about the
    // camera while waiting or refused prints underneath it.
    const getVideoMessageKey = () => {
        if (ownWaitingRoomStanding) {
            return null;
        }
        // Null until checkHasDevicePermission answers, which is not the same as
        // being refused — saying so before asking accuses the browser of
        // something it has not done, then takes it back a moment later.
        if (videoAllowed === null) {
            return null;
        }
        if (!videoAllowed) {
            return 'videoconference.lobby.camera_no_rights';
        }
        if (cameraError) {
            return 'videoconference.lobby.camera_error';
        }
        if (!videoActive) {
            return 'videoconference.lobby.camera_off';
        }
        return null;
    };
    const videoMessageKey = getVideoMessageKey();

    return (
        <div className={VIDEO_CONTAINER_CLASS}>
            <video
                className="size-full -scale-x-100 rounded-2xl object-cover md:h-[360px] md:w-[640px]"
                ref={videoRef}
                autoPlay={true}
                muted={true}
            />
            <div className="absolute inset-0 flex items-center justify-center p-3 text-white">
                {videoMessageKey ? (
                    <div className="p-3">{i18next.t(videoMessageKey)}</div>
                ) : null}
            </div>
            <MeetingLobbyAdmissionNotice />

            <div className="absolute inset-x-0 bottom-4 flex items-center justify-center gap-2">
                <MeetingLobbyDeviceControls />
                <Tooltip label={deviceSettingsTitle}>
                    <PopoverMenu
                        popoverClassName={LOBBY_CONTROL_CLASS}
                        placement="bottom-end"
                        icon={<Icon iconKey="general" size="1.5rem" />}
                        triggerLabel={deviceSettingsTitle}
                    >
                        <div className="box-border w-full rounded-sm border bg-white p-6 dark:bg-gray-100">
                            <MeetingDeviceSettings
                                meetingLocation={MeetingLocation.LOBBY}
                            />
                        </div>
                    </PopoverMenu>
                </Tooltip>
            </div>
            {!hasActiveMeeting && audioActive && audioStream ? (
                <div className="absolute start-3 bottom-3 flex size-9 items-center justify-center rounded-sm bg-black/50 text-white focus:outline-hidden">
                    <MeetingAudioIndicator
                        audioStream={audioStream}
                        meetingLocation={MeetingLocation.LOBBY}
                    />
                </div>
            ) : null}
        </div>
    );
};

export default MeetingLobbyVideo;
